import { API_BASE, lastCodeRequest } from './client'

/**
 * Whether this build is allowed to read a delivered code back.
 *
 * <p>True in a Vite dev server, and true in a build made with `VITE_DEMO=1` —
 * which is what `infra/docker-compose.dev.yml` produces. That second case is
 * the whole reason this is not just `import.meta.env.DEV`: the hosted stack
 * runs `pnpm -r build`, so `DEV` is false there and every "récupérer le code"
 * button disappeared from exactly the environment a demo is given from.
 *
 * <p>It stays a *build* flag rather than a runtime one, so a production bundle
 * cannot be talked into demo behaviour. And it is not the security control in
 * any case: `/v1/dev/**` exists only under the `local` and `ci` profiles, so
 * against a real backend this button finds nothing however it was built.
 */
const DEMO_BUILD: boolean = import.meta.env.DEV || import.meta.env.VITE_DEMO === '1'

/**
 * The code that was just delivered to `destination`, for development only.
 *
 * <p>Every code is hashed at rest — correctly — so there is no way to read one
 * back, which also means nobody can get past an OTP screen without a real
 * phone or mailbox. Under the `local` and `ci` profiles the senders remember
 * the last code per destination and `/v1/dev/last-code` hands it back. In
 * production neither the bean nor the route exists, so this returns null and
 * the button that calls it is not rendered anyway.
 *
 * <p>Polled, because the code is recorded as the SMS or mail is sent — a moment
 * after the response the browser already has.
 *
 * @param notBefore epoch milliseconds. A code delivered before this is ignored
 *   and polling continues. Defaults to the moment this session last asked for a
 *   code, which the client tracks: the log keeps only the last code per
 *   destination, so without it the previous — already consumed — code comes
 *   back instantly and the screen refuses it.
 */
export async function fetchDevCode(
  destination: string,
  notBefore = lastCodeRequest(),
): Promise<string | null> {
  if (!DEMO_BUILD) return null

  // Slack, because `notBefore` is read off the browser clock and compared
  // against the server's. They agree to well under a second in every
  // environment this runs in, and a stale code is minutes old, not seconds.
  const since = notBefore <= 0 ? '' : `&notBefore=${notBefore - 2000}`
  for (let poll = 0; poll < 20; poll++) {
    try {
      const res = await fetch(
        `${API_BASE}/dev/last-code?destination=${encodeURIComponent(destination)}${since}`,
      )
      if (res.ok) {
        return ((await res.json()) as { code: string }).code
      }
    } catch {
      // The route does not exist outside development; treat it as "no code".
      return null
    }
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
  return null
}

/** The guard on every caller's button. See {@link DEMO_BUILD}. */
export function devCodeAvailable(): boolean {
  return DEMO_BUILD
}
